// Lazy engine-asset loading (D-013 / D-027). The heavy, correctness-critical engines — the
// Saxon-JS runtime (~488 KB), the compiled Schematron SEFs (UBL ~11 MB, CII ~8 MB), the XSD
// bundles (UBL ~1 MB) and the visualization SEFs — are NEVER in the initial chunk. They live
// under public/engines/ (staged from vendor/ by scripts/prepare-assets.mjs) and are fetched
// on demand the first time a file is validated. All fetches are same-origin only (§3.1) and
// results are cached so a second file reuses them; a service worker (Phase 2) precaches them
// for offline use.
//
// URLs go through BASE_URL so the app also works when deployed under a sub-path.

import type { Syntax } from './types';

export interface XsdFile {
  fileName: string;
  contents: string;
}

export interface SaxonTransformOptions {
  stylesheetInternal: unknown;
  sourceText: string;
  destination: 'serialized';
  /** Absolute base URI for resolving relative unparsed-text()/doc() calls in the stylesheet (D-035). */
  stylesheetBaseURI?: string;
}
export interface SaxonJsApi {
  transform(opts: SaxonTransformOptions, mode: 'sync'): { principalResult: string };
}

export function engineUrl(name: string): string {
  return `${import.meta.env.BASE_URL}engines/${name}`;
}

/**
 * Absolute (scheme+host) URL of the engines/ directory, WITH a trailing slash. Saxon-JS's
 * `stylesheetBaseURI` transform option needs a fully-qualified base — a bare relative path is not
 * enough to resolve the visualization stylesheet's relative unparsed-text()/doc() calls (D-035).
 */
export function engineBaseUri(): string {
  return new URL(engineUrl(''), window.location.href).href;
}

/** URL of the Firefox-safe classic xmllint worker (patched by prepare-assets, D-020). */
export const xmllintWorkerUrl = (): string => engineUrl('xmllint-browser-classic.js');
/** URL of the pdf.js worker (version-matched to the bundled pdfjs-dist, staged by prepare-assets). */
export const pdfWorkerUrl = (): string => engineUrl('pdf.worker.mjs');

// --- Saxon-JS runtime: a classic global <script>, loaded once ---
let _saxonPromise: Promise<SaxonJsApi> | null = null;
export function loadSaxon(): Promise<SaxonJsApi> {
  if (_saxonPromise) return _saxonPromise;
  _saxonPromise = new Promise<SaxonJsApi>((resolve, reject) => {
    const existing = (globalThis as { SaxonJS?: SaxonJsApi }).SaxonJS;
    if (existing) return resolve(existing);
    const s = document.createElement('script');
    s.src = engineUrl('SaxonJS2.rt.js');
    s.async = true;
    s.onload = () => {
      const api = (globalThis as { SaxonJS?: SaxonJsApi }).SaxonJS;
      if (api) resolve(api);
      else reject(new Error('SaxonJS2.rt.js loaded but window.SaxonJS is undefined'));
    };
    s.onerror = () => reject(new Error('failed to load SaxonJS2.rt.js'));
    document.head.appendChild(s);
  });
  // Never cache a rejection: a transient failure must not poison the whole session (D-034).
  _saxonPromise.catch(() => {
    _saxonPromise = null;
  });
  return _saxonPromise;
}

// --- Compiled SEFs (Schematron + visualization), fetched as JSON, cached ---
const _sefCache = new Map<string, Promise<unknown>>();
export function loadSef(name: string): Promise<unknown> {
  let p = _sefCache.get(name);
  if (!p) {
    p = fetch(engineUrl(name)).then((r) => {
      if (!r.ok) throw new Error(`SEF not available: ${name} (${r.status})`);
      return r.json();
    });
    p.catch(() => _sefCache.delete(name)); // don't cache a transient failure (D-034)
    _sefCache.set(name, p);
  }
  return p;
}

/** Whether a staged engine asset exists (used to feature-detect the optional visualization). */
export async function engineAssetExists(name: string): Promise<boolean> {
  try {
    const r = await fetch(engineUrl(name), { method: 'HEAD' });
    return r.ok;
  } catch {
    return false;
  }
}

// --- XSD bundles (flattened schema trees), fetched as JSON, cached ---
const _xsdCache = new Map<Syntax, Promise<XsdFile[]>>();
export function loadXsdBundle(syntax: Syntax): Promise<XsdFile[]> {
  let p = _xsdCache.get(syntax);
  if (!p) {
    const name = syntax === 'CII' ? 'cii-xsd-bundle.json' : 'ubl-xsd-bundle.json';
    p = fetch(engineUrl(name)).then((r) => {
      if (!r.ok) throw new Error(`XSD bundle not available: ${name} (${r.status})`);
      return r.json();
    });
    p.catch(() => _xsdCache.delete(syntax)); // don't cache a transient failure (D-034)
    _xsdCache.set(syntax, p);
  }
  return p;
}
